"use client"

import {
  Button,
  Checkbox,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  cn,
} from "@rarible_int/anaconda_ui_components"
import {
  gachaCtaButtonRowPrimaryClassName,
  gachaCtaButtonRowSecondaryClassName,
  gachaDialogFooterClassName,
} from "./gacha-cta-button-styles"
import { GachaDialogContent } from "./gacha-dialog-content"

const GACHA_DISCLAIMER_LINK_CLASSNAME =
  "text-brand underline underline-offset-2 hover:opacity-80"

export const GACHA_TERMS_URL = "/terms"
const COLLECTOR_CRYPT_URL = "https://collectorcrypt.com"
const SUPPORT_EMAIL = "support@rarible.com"

export const GACHA_PURCHASE_DISCLAIMER_AGREEMENT =
  "I understand the risks and agree to proceed under these terms."

export const GACHA_PURCHASE_DISCLAIMER_PARAGRAPHS = [
  'You acknowledge and agree that Gacha Cards are provided and fulfilled solely by Collector Crypt Inc. ("Collector Crypt"), and not by Rarible. By purchasing or interacting with a Gacha Card, you agree to be bound by Collector Crypt\'s terms and conditions, available at collectorcrypt.com, as in effect at the time of your purchase. All Gacha Card fulfilment, buyback offers, redemption programmes, delivery, shipping, and importation of any underlying real-world assets ("RWAs") are performed exclusively by Collector Crypt and not by Rarible. We do not control and bear no responsibility or liability for the underlying RWAs or Gacha Cards, including but not limited to: (i) the authenticity, grading, or condition of any RWA; (ii) the storage or safekeeping of RWAs or Gacha Cards; (iii) the terms, availability, or performance of any buyback offers, redemption programmes, delivery, shipping, or importation of RWAs, all of which are determined and carried out solely by Collector Crypt; or (iv) any related taxes, duties, or customs compliance. Rarible does not develop, operate, audit, verify, control, or assume responsibility for any systems, smart contracts, software, applications, infrastructure, services, mechanics, fulfilment processes, redemption processes, buyback programmes, or other functionality provided by Collector Crypt or any third party in connection with Gacha Cards or underlying RWAs. Rarible shall have no liability for any bugs, vulnerabilities, exploits, hacks, security incidents, technical failures, coding errors, smart contract failures, data inaccuracies, service interruptions, insolvency events, misconduct, negligence, fraud, or other acts or omissions of Collector Crypt or any third party, nor for any resulting loss, damage, delay, inability to redeem or claim assets, loss of assets, or other adverse consequences. All such matters are exclusively between you and Collector Crypt. Any claims or disputes regarding Gacha Cards, buyback offers, redemptions, or underlying RWAs must be directed to Collector Crypt. We may, at our sole discretion, relay communications between you and Collector Crypt through our customer support channels for convenience only, and such assistance shall not be construed as assumption of any dispute resolution role or create any obligation or liability on our part.',
  "Nothing on the Rarible Platform constitutes, or should be construed as, gambling, wagering, betting, lottery, or gaming activities conducted or operated by Rarible. Rarible's role is limited to providing the Platform and related marketplace infrastructure. Participation in any Gacha Card offering, and any purchase, holding, transfer, sale, redemption, or other interaction involving NFTs, digital assets, RWAs, or related products, involves significant risks, including the risk of partial or total loss of value. Rarible makes no representation, warranty, or guarantee that you will be able to recover any amounts paid, resell any asset, receive any particular item or outcome, obtain any return, or avoid losses. Any prices, valuations, rarity rankings, buyback amounts, estimated values, projections, statistics, drop rates, probabilities, or other figures displayed on the Platform are provided for informational purposes only, may be determined or supplied by Collector Crypt or other third parties, are inherently uncertain and subject to change, and must not be relied upon as guarantees of future outcomes, performance, value, or liquidity.",
  "Rarible does not create, select, verify, endorse, review, monitor, or assume responsibility for any intellectual property, trademarks, copyrights, branding, artwork, images, content, descriptions, listings, metadata, or other materials associated with any Gacha Card, underlying RWA, or related content made available by Collector Crypt, its licensors or subcontractors, users, or any other third party. Any use of third-party intellectual property is solely the responsibility of the relevant party providing or making such content available. The Rarible platform may display references to third-party brands, trademarks, trade names, logos, characters, artwork, product names, and other intellectual property solely for purposes of identifying, describing, categorizing, indexing, searching for, discovering, or facilitating user-generated content and marketplace activity. All such intellectual property is the property of its respective owner. Rarible does not own nor claim any rights in such intellectual property, and the display of any such materials does not imply any affiliation, sponsorship, endorsement, authorization, or approval by the applicable rights holder. If you believe that any content, Gacha Card, RWA, listing, artwork, or other material available through the Rarible platform infringes your intellectual property rights, please submit a written notice to support@rarible.com in accordance with the procedures set out in our Terms of Service.",
  "You are solely responsible for ensuring that you are not located in, incorporated in, or otherwise associated with any jurisdiction restricted by Rarible, Collector Crypt, or any relevant third parties, and for complying with all applicable laws. Any failure to comply may result in consequences, including without limitation suspension or termination of platform access, forfeiture of digital assets, NFTs and/or related benefits, and permanent exclusion from future participation, in each case without refund, compensation, or recourse. You agree to bear full legal liability for any consequences arising from non-compliance. By participating, you are solely responsible for all associated risks and agree to be bound by the provisions in Rarible's Terms of Service.",
] as const

type GachaPurchaseDisclaimerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: React.ReactNode
  agreed: boolean
  onAgreedChange: (agreed: boolean) => void
  onConfirm: () => void
  isSubmitting?: boolean
  errorMessage?: string | null
}

export function GachaPurchaseDisclaimerDialog({
  open,
  onOpenChange,
  title = "Before you purchase",
  agreed,
  onAgreedChange,
  onConfirm,
  isSubmitting = false,
  errorMessage = null,
}: GachaPurchaseDisclaimerDialogProps) {
  const handleCancel = () => {
    if (isSubmitting) return
    onOpenChange(false)
  }

  const handleConfirm = () => {
    if (!agreed || isSubmitting) return
    onConfirm()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <GachaDialogContent
        className="flex max-h-[min(88dvh,720px)] max-w-[560px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[600px]"
        data-testid="gacha-purchase-disclaimer-dialog"
      >
        <DialogHeader className="shrink-0 space-y-2 p-5 pb-5 text-left">
          <DialogTitle className="font-headline font-normal text-2xl leading-tight tracking-tight text-foreground">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-2">
          <div className="space-y-4 text-xs leading-relaxed text-foreground/70">
            <p>
              You acknowledge and agree that Gacha Cards are provided and
              fulfilled solely by Collector Crypt Inc. (&quot;Collector
              Crypt&quot;), and not by Rarible. By purchasing or interacting
              with a Gacha Card, you agree to be bound by Collector Crypt&apos;s
              terms and conditions, available at{" "}
              <a
                href={COLLECTOR_CRYPT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={GACHA_DISCLAIMER_LINK_CLASSNAME}
              >
                collectorcrypt.com
              </a>
              , as in effect at the time of your purchase. All Gacha Card
              fulfilment, buyback offers, redemption programmes, delivery,
              shipping, and importation of any underlying real-world assets
              (&quot;RWAs&quot;) are performed exclusively by Collector Crypt
              and not by Rarible. We do not control and bear no responsibility
              or liability for the underlying RWAs or Gacha Cards, including but
              not limited to: (i) the authenticity, grading, or condition of any
              RWA; (ii) the storage or safekeeping of RWAs or Gacha Cards; (iii)
              the terms, availability, or performance of any buyback offers,
              redemption programmes, delivery, shipping, or importation of RWAs,
              all of which are determined and carried out solely by Collector
              Crypt; or (iv) any related taxes, duties, or customs compliance.
              Rarible does not develop, operate, audit, verify, control, or
              assume responsibility for any systems, smart contracts, software,
              applications, infrastructure, services, mechanics, fulfilment
              processes, redemption processes, buyback programmes, or other
              functionality provided by Collector Crypt or any third party in
              connection with Gacha Cards or underlying RWAs. Rarible shall have
              no liability for any bugs, vulnerabilities, exploits, hacks,
              security incidents, technical failures, coding errors, smart
              contract failures, data inaccuracies, service interruptions,
              insolvency events, misconduct, negligence, fraud, or other acts or
              omissions of Collector Crypt or any third party, nor for any
              resulting loss, damage, delay, inability to redeem or claim
              assets, loss of assets, or other adverse consequences. All such
              matters are exclusively between you and Collector Crypt. Any
              claims or disputes regarding Gacha Cards, buyback offers,
              redemptions, or underlying RWAs must be directed to Collector
              Crypt. We may, at our sole discretion, relay communications
              between you and Collector Crypt through our customer support
              channels for convenience only, and such assistance shall not be
              construed as assumption of any dispute resolution role or create
              any obligation or liability on our part.
            </p>

            <p>{GACHA_PURCHASE_DISCLAIMER_PARAGRAPHS[1]}</p>

            <p>
              Rarible does not create, select, verify, endorse, review, monitor,
              or assume responsibility for any intellectual property,
              trademarks, copyrights, branding, artwork, images, content,
              descriptions, listings, metadata, or other materials associated
              with any Gacha Card, underlying RWA, or related content made
              available by Collector Crypt, its licensors or subcontractors,
              users, or any other third party. Any use of third-party
              intellectual property is solely the responsibility of the relevant
              party providing or making such content available. The Rarible
              platform may display references to third-party brands, trademarks,
              trade names, logos, characters, artwork, product names, and other
              intellectual property solely for purposes of identifying,
              describing, categorizing, indexing, searching for, discovering, or
              facilitating user-generated content and marketplace activity. All
              such intellectual property is the property of its respective
              owner. Rarible does not own nor claim any rights in such
              intellectual property, and the display of any such materials does
              not imply any affiliation, sponsorship, endorsement,
              authorization, or approval by the applicable rights holder. If you
              believe that any content, Gacha Card, RWA, listing, artwork, or
              other material available through the Rarible platform infringes
              your intellectual property rights, please submit a written notice
              to{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={GACHA_DISCLAIMER_LINK_CLASSNAME}
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              in accordance with the procedures set out in our{" "}
              <a
                href={GACHA_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={GACHA_DISCLAIMER_LINK_CLASSNAME}
              >
                Terms of Service
              </a>
              .
            </p>

            <p>
              You are solely responsible for ensuring that you are not located
              in, incorporated in, or otherwise associated with any jurisdiction
              restricted by Rarible, Collector Crypt, or any relevant third
              parties, and for complying with all applicable laws. Any failure
              to comply may result in consequences, including without limitation
              suspension or termination of platform access, forfeiture of
              digital assets, NFTs and/or related benefits, and permanent
              exclusion from future participation, in each case without refund,
              compensation, or recourse. You agree to bear full legal liability
              for any consequences arising from non-compliance. By
              participating, you are solely responsible for all associated risks
              and agree to be bound by the provisions in Rarible&apos;s{" "}
              <a
                href={GACHA_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={GACHA_DISCLAIMER_LINK_CLASSNAME}
              >
                Terms of Service
              </a>
              .
            </p>
          </div>
        </div>

        <div className="shrink-0 space-y-4 border-foreground/10 border-t p-5">
          <div className="flex items-start gap-3">
            <Checkbox
              id="gacha-purchase-disclaimer-agree"
              checked={agreed}
              disabled={isSubmitting}
              onCheckedChange={(checked) => onAgreedChange(checked === true)}
              data-testid="gacha-purchase-disclaimer-agree"
            />
            <Label
              htmlFor="gacha-purchase-disclaimer-agree"
              className="cursor-pointer text-sm leading-snug text-foreground/80"
            >
              {GACHA_PURCHASE_DISCLAIMER_AGREEMENT}
            </Label>
          </div>

          {errorMessage ? (
            <p
              className="text-destructive text-sm leading-snug"
              data-testid="gacha-purchase-disclaimer-error"
            >
              {errorMessage}
            </p>
          ) : null}

          <DialogFooter className={gachaDialogFooterClassName}>
            <Button
              type="button"
              variant="outline"
              className={gachaCtaButtonRowSecondaryClassName}
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              className={cn(
                gachaCtaButtonRowPrimaryClassName,
                "sm:whitespace-normal",
              )}
              disabled={!agreed || isSubmitting}
              onClick={handleConfirm}
              data-testid="gacha-purchase-disclaimer-continue"
            >
              {isSubmitting ? "Continuing..." : "Continue"}
            </Button>
          </DialogFooter>
        </div>
      </GachaDialogContent>
    </Dialog>
  )
}
